import { Command } from "@oclif/core"
import { DTODesignSystem, SupernovaApiClient } from "@supernova-studio/client"
import inquirer from "inquirer"
import { z } from "zod"

import { getApiClient } from "../utils/api-client.js"
import { SupernovaConfigService } from "../utils/config.service.js"
import { getTargetEnv, TargetEnv } from "./environment.js"

const hasAccess = (role?: DTODesignSystem["role"]) =>
  role && ["Admin", "Contributor", "Creator", "Owner"].includes(role)

export abstract class BaseCommand<T extends object> extends Command {
  protected env: TargetEnv = getTargetEnv()
  protected configService = SupernovaConfigService.getInstance()
  private _apiClient: SupernovaApiClient | undefined

  abstract get commandId(): string
  abstract get configSchema(): z.ZodType<T>

  protected async apiClient() {
    if (this._apiClient) return this._apiClient

    const apiClient = await getApiClient(this.env)
    this._apiClient = apiClient
    return apiClient
  }

  protected async init() {
    await super.init()
    this.log(`Using ${this.env} environment`)
  }

  protected async promptBrandId(designSystemId: string, versionId?: string): Promise<string> {
    const client = await this.apiClient()
    const brandsEndpoint = client.designSystems.versions.brands

    const { brands } = await brandsEndpoint.list(designSystemId, versionId ?? "head")

    const options = brands.map(b => ({ name: b.meta.name, value: b.persistentId }))
    if (options.length === 1) return options[0].value

    return this.prompt("Select a brand:", options)
  }

  protected async promptDesignSystemId(): Promise<string> {
    const { designSystems: client } = await this.apiClient()
    const { designSystems, workspaces } = await client.listUserDesignSystems()
    const workspaceById = new Map(workspaces.map(ws => [ws.id, ws]))
    const dsNameCount = designSystems.reduce((acc, { meta: { name }, role }) => {
      if (hasAccess(role)) acc.set(name, (acc.get(name) ?? 0) + 1)

      return acc
    }, new Map<string, number>())

    if (designSystems.length === 0) {
      this.error("You don't have any design system.")
    }

    const dsIds = designSystems
      .sort(({ meta: { name: a } }, { meta: { name: b } }) => a.localeCompare(b))
      .map(({ id, meta: { name }, role, workspaceId }) => ({
        id,
        name: (dsNameCount.get(name) ?? 0) > 1 ? `${name} (${workspaceById.get(workspaceId)?.profile?.name})` : name,
        role,
      }))
      .filter(({ role }) => hasAccess(role))

    if (dsIds.length === 0) {
      this.error("You don't have access to any design system.")
    }

    if (dsIds.length === 1) {
      return dsIds[0].id
    }

    return this.prompt(
      "Select a design system:",
      dsIds.map(({ id, name }) => ({
        name,
        value: id,
      })),
    )
  }

  protected async getVersionId(designSystemId: string, versionId?: string): Promise<string> {
    if (versionId) return versionId
    const { versions } = (await this.apiClient()).designSystems
    const verIds = (await versions.list(designSystemId)).designSystemVersions.map(({ id, meta: { name } }) => ({
      id,
      name,
    }))

    if (verIds.length === 1) {
      return verIds[0].id
    }

    return this.prompt(
      "Select a version:",
      verIds.map(wrk => ({
        name: wrk.name,
        value: wrk.id,
      })),
    )
  }

  protected async getWorkspaceId(workspaceId?: string): Promise<string> {
    if (workspaceId) return workspaceId
    const { workspaces } = await this.apiClient()
    const wrks = (await workspaces.list()).membership.map(
      ({
        workspace: {
          id,
          profile: { name },
        },
      }) => ({ id, name }),
    )

    if (wrks.length === 1) {
      return wrks[0].id
    }

    return this.prompt(
      "Select a workspace:",
      wrks.map(wrk => ({
        name: wrk.name,
        value: wrk.id,
      })),
    )
  }

  private async prompt(message: string, choices: { name: string; value: string }[]) {
    const choice = await inquirer.prompt([
      {
        choices: [...choices, { name: "Exit", value: "exit" }],
        message,
        name: "selected",
        type: "list",
      },
    ])

    return choice.selected as string
  }
}
